import * as React from "react";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { cn } from "../cn";

type Tone = "neutral" | "warn" | "bad" | "ok";
type Direction = "up" | "down" | "flat";

export interface StatProps {
  label: string;
  value: string;
  /** Small muted unit code rendered after the figure (e.g. "UGX"). */
  unit?: string;
  /** Sub-line / delta text below the figure. */
  sub?: string;
  subTone?: Tone;
  /** Optional delta chip rendered next to the figure (e.g. "+5.3%"). */
  delta?: string;
  deltaTone?: Tone;
  /** Structured delta: drives an arrow glyph + colour. Overrides deltaTone. */
  deltaDirection?: Direction;
  /** For cost/exposure metrics: render down=green, up=red (judgement, not maths). */
  invertDelta?: boolean;
  /** A faint comparison line under the sub (e.g. "vs last quarter"). */
  compare?: string;
  /** Optional trailing slot (sparkline, progress bar). */
  trend?: React.ReactNode;
  className?: string;
  /** Renders the figure at hero scale. */
  hero?: boolean;
  onClick?: () => void;
}

const toneClass: Record<Tone, string> = {
  neutral: "text-faint",
  warn: "text-warn",
  bad: "text-bad",
  ok: "text-ok",
};

// -strong text on soft fill so warn/danger deltas clear AA.
const chipClass: Record<Tone, string> = {
  neutral: "bg-neutralSoft text-mute",
  warn: "bg-warnSoft text-warnStrong",
  bad: "bg-badSoft text-badStrong",
  ok: "bg-okSoft text-okStrong",
};

function directionTone(dir: Direction, invert?: boolean): Tone {
  if (dir === "flat") return "neutral";
  if (invert) return dir === "down" ? "ok" : "bad";
  return dir === "up" ? "ok" : "bad";
}

export function Stat({
  label,
  value,
  unit,
  sub,
  subTone = "neutral",
  delta,
  deltaTone = "neutral",
  deltaDirection,
  invertDelta,
  compare,
  trend,
  className,
  hero,
  onClick,
}: StatProps) {
  const Comp = onClick ? "button" : "div";
  const effTone = deltaDirection ? directionTone(deltaDirection, invertDelta) : deltaTone;
  const Arrow = deltaDirection === "up" ? ArrowUp : deltaDirection === "down" ? ArrowDown : deltaDirection === "flat" ? Minus : null;
  return (
    <Comp
      onClick={onClick}
      className={cn(
        "flex flex-col rounded-card border border-line bg-card p-4 text-left transition-colors duration-200",
        onClick && "focus-ring cursor-pointer hover:border-strong",
        className,
      )}
    >
      <span className="text-meta text-faint">{label}</span>
      <span className="mt-2.5 flex items-baseline gap-1.5">
        <span className={cn("num whitespace-nowrap font-semibold text-ink", hero ? "text-bigfig" : "text-figure")}>{value}</span>
        {unit && <span className="text-sm font-medium text-faint">{unit}</span>}
        {delta && (
          <span className={cn("ml-auto inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium num", chipClass[effTone])}>
            {Arrow && <Arrow className="h-3 w-3" aria-hidden />}
            {delta}
          </span>
        )}
      </span>
      {sub && <span className={cn("mt-2 text-xs", toneClass[subTone])}>{sub}</span>}
      {compare && <span className="mt-0.5 text-[11px] text-faint">{compare}</span>}
      {trend && <div className="mt-3">{trend}</div>}
    </Comp>
  );
}

/** Lightweight inline sparkline for KPI tiles; optional dashed target/baseline rule. */
export function Sparkline({
  data,
  tone = "neutral",
  className,
  width = 96,
  height = 28,
  target,
}: {
  data: number[];
  /** Default is slate; use ok/warn/bad only when the metric has a defined good direction. */
  tone?: "brand" | "ok" | "warn" | "bad" | "neutral";
  className?: string;
  width?: number;
  height?: number;
  /** A reference value drawn as a faint dashed rule (e.g. plan / target). */
  target?: number;
}) {
  if (data.length < 2) return null;
  const min = Math.min(...data, ...(target != null ? [target] : []));
  const max = Math.max(...data, ...(target != null ? [target] : []));
  const span = max - min || 1;
  const stepX = width / (data.length - 1);
  const yFor = (v: number) => height - ((v - min) / span) * (height - 4) - 2;
  const points = data.map((d, i) => [i * stepX, yFor(d)] as const);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const stroke = {
    brand: "var(--accent-base)",
    ok: "var(--status-success-fill)",
    warn: "var(--status-warn-fill)",
    bad: "var(--status-danger-fill)",
    neutral: "var(--status-neutral-fill)",
  }[tone];
  const last = points[points.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={className} fill="none" aria-hidden>
      {target != null && (
        <line x1={0} x2={width} y1={yFor(target)} y2={yFor(target)} stroke="var(--border-strong)" strokeWidth={1} strokeDasharray="3 3" />
      )}
      <path d={`${path} L${width},${height} L0,${height} Z`} fill={stroke} fillOpacity={0.08} />
      <path d={path} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={2} fill={stroke} />
    </svg>
  );
}

/** Inline label + figure + delta row. */
export function MetricRow({
  label,
  value,
  delta,
  deltaTone = "neutral",
  className,
}: {
  label: string;
  value: string;
  delta?: string;
  deltaTone?: "neutral" | "warn" | "bad" | "ok";
  className?: string;
}) {
  return (
    <div className={cn("flex items-baseline justify-between gap-4 py-1.5", className)}>
      <span className="text-sm text-mute">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className="text-sm font-medium tabular-nums text-ink">{value}</span>
        {delta && <span className={cn("text-xs tabular-nums", toneClass[deltaTone])}>{delta}</span>}
      </span>
    </div>
  );
}

/** Thin progress bar used inside Stats and tables. */
export function ProgressBar({
  value,
  className,
  tone = "brand",
  label,
}: {
  value: number; // 0..1
  className?: string;
  tone?: "brand" | "ok" | "warn" | "bad" | "info";
  /** Accessible name describing what the bar measures (e.g. "82% of spend evidenced"). */
  label?: string;
}) {
  const toneBg = {
    brand: "bg-brand",
    ok: "bg-okFill",
    warn: "bg-warnFill",
    bad: "bg-badFill",
    info: "bg-infoFill",
  }[tone];
  const pct = Math.min(100, Math.max(0, value * 100));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-subtle", className)}
    >
      <div className={cn("h-full rounded-full", toneBg)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * One combined spend-vs-budget card: a single human line "Spent X of Y", a brand-tone
 * progress bar at spent/budget, and a trailing percent. Burn is never RAG-coloured — the
 * bar stays brand/neutral regardless of how hot the spend is (health is a separate pill).
 * Replaces every separate Budgeted + Spent stat pair. Pass pre-formatted money strings so
 * this primitive stays currency-agnostic (caller uses useMoney / formatHumanCurrency).
 */
export function SpendBudgetStat({
  spent,
  budget,
  spentLabel,
  budgetLabel,
  label = "Spend",
  className,
}: {
  /** Raw spent figure (UGX base), used only for the ratio. */
  spent: number;
  /** Raw budget figure (UGX base); the bar/percent guard against budget <= 0. */
  budget: number;
  /** Human-formatted spent string, e.g. "71M UGX". */
  spentLabel: string;
  /** Human-formatted budget string, e.g. "152M UGX". */
  budgetLabel: string;
  /** Accessible/eyebrow label for the row. */
  label?: string;
  className?: string;
}) {
  const ratio = budget > 0 ? spent / budget : 0;
  const pct = Math.round(Math.min(100, Math.max(0, ratio * 100)));
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="text-mute">
          Spent <span className="font-medium tabular-nums text-ink">{spentLabel}</span> of{" "}
          <span className="font-medium tabular-nums text-ink">{budgetLabel}</span>
        </span>
        <span className="whitespace-nowrap font-medium tabular-nums text-mute">{pct}%</span>
      </div>
      <ProgressBar value={ratio} tone="brand" label={`${label}: ${spentLabel} of ${budgetLabel} (${pct}%)`} />
    </div>
  );
}

// One source of truth: a RAG/semantic tone drives icon colour, text colour and bar fill
// together, so a label, value and colour can never disagree (brief §4.1).
export type StatusTone = "success" | "warn" | "danger" | "info" | "neutral";

const STATUS_TONE: Record<StatusTone, { text: string; bar: string; icon: string }> = {
  success: { text: "text-okStrong", bar: "bg-okFill", icon: "text-ok" },
  warn: { text: "text-warnStrong", bar: "bg-warnFill", icon: "text-warn" },
  danger: { text: "text-badStrong", bar: "bg-badFill", icon: "text-bad" },
  info: { text: "text-infoStrong", bar: "bg-infoFill", icon: "text-info" },
  neutral: { text: "text-mute", bar: "bg-neutralFill", icon: "text-faint" },
};

/** Map a GREEN/AMBER/RED state to a semantic tone. */
export function toneFromRag(rag: "GREEN" | "AMBER" | "RED"): StatusTone {
  return rag === "GREEN" ? "success" : rag === "AMBER" ? "warn" : "danger";
}

/**
 * A single self-consistent status row: one tone drives the icon, the worded value and
 * the bar fill. Replaces hand-rolled gate/health rows where copy, colour and bar could
 * drift apart.
 */
export function StatusRow({
  icon: Icon,
  label,
  value,
  tone,
  fill,
  target,
  className,
}: {
  icon?: React.ElementType;
  label: string;
  value: string;
  tone: StatusTone;
  /** 0..1 — when provided, renders a thin bar in the tone fill beneath the row. */
  fill?: number;
  /** 0..1 — draws a target tick on the bar (e.g. the gate evidence threshold). */
  target?: number;
  className?: string;
}) {
  const t = STATUS_TONE[tone];
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-3 text-[13px]">
        <span className="inline-flex items-center gap-1.5 text-mute">
          {Icon && <Icon className={cn("h-3.5 w-3.5", t.icon)} aria-hidden />}
          {label}
        </span>
        <span className={cn("whitespace-nowrap font-medium tabular-nums", t.text)}>{value}</span>
      </div>
      {fill != null && (
        <div className="relative">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-vizTrack" role="progressbar" aria-valuenow={Math.round(Math.min(100, Math.max(0, fill * 100)))} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${value}`}>
            <div className={cn("h-full rounded-full", t.bar)} style={{ width: `${Math.max(4, Math.min(100, fill * 100))}%` }} />
          </div>
          {target != null && (
            <span
              className="absolute top-1/2 h-2.5 w-0.5 -translate-y-1/2 rounded-full bg-faint"
              style={{ left: `${Math.min(100, Math.max(0, target * 100))}%` }}
              aria-hidden
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A progress bar that carries its own label and value+denominator caption, so the
 * "47% of budget" figure always travels with the bar (brief §2.3 / §4).
 */
export function LabeledBar({
  label,
  caption,
  value,
  tone = "brand",
  className,
}: {
  label: string;
  /** Right-aligned figure with its denominator, e.g. "71K of 152K" or "47% of budget". */
  caption: string;
  value: number; // 0..1
  tone?: "brand" | "ok" | "warn" | "bad";
  className?: string;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="text-mute">{label}</span>
        <span className="whitespace-nowrap tabular-nums text-ink">{caption}</span>
      </div>
      <ProgressBar value={value} tone={tone} label={`${label}: ${caption}`} />
    </div>
  );
}
